import React from 'react';
import { Text, View, StyleSheet } from '@react-pdf/renderer';

const styles = StyleSheet.create({
    section: {
        marginTop: 8,
        padding: 6,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: '#cccccc',
        borderRadius: 5,
        backgroundColor: '#f8f9fa',
    },
    sectionTitle: {
        fontSize: 11,
        fontFamily: 'Helvetica-Bold',
        marginBottom: 6,
        paddingBottom: 3,
        borderBottomWidth: 1,
        borderBottomStyle: 'solid',
        borderBottomColor: '#dee2e6',
        color: '#495057',
        textAlign: 'center',
    },
    compressorHeader: {
        fontSize: 10,
        fontFamily: 'Helvetica-Bold',
        color: '#495057',
        marginBottom: 4,
        marginTop: 6,
        paddingBottom: 2,
        borderBottomWidth: 1,
        borderBottomStyle: 'solid',
        borderBottomColor: '#dee2e6',
    },
    checklistRow: {
        flexDirection: 'row',
        marginBottom: 3,
        alignItems: 'center',
    },
    checklistGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        marginBottom: 6,
    },
    checklistItemHorizontal: {
        width: '50%',
        flexDirection: 'row',
        marginBottom: 3,
        alignItems: 'center',
    },
    checkboxContainer: {
        width: 8,
        height: 8,
        borderWidth: 0.5,
        borderStyle: 'solid',
        borderColor: '#000000',
        marginRight: 4,
        textAlign: 'center',
    },
    checkboxLabel: {
        fontSize: 8,
        fontFamily: 'Helvetica',
        color: '#212529',
        flex: 1,
    },
    measurementsGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        marginTop: 4,
    },
    measurementItem: {
        width: '33%',
        flexDirection: 'row',
        marginBottom: 2,
        alignItems: 'center',
    },
    measurementLabel: {
        fontSize: 8,
        fontFamily: 'Helvetica-Bold',
        color: '#495057',
        width: 60,
    },
    measurementValue: {
        fontSize: 8,
        fontFamily: 'Helvetica',
        color: '#212529',
        flex: 1,
    },
    noDataText: {
        fontSize: 9,
        fontStyle: 'italic',
        color: '#6c757d',
        textAlign: 'center',
        marginTop: 6,
    },
});

interface CompressorSectionProps {
    compressors: Array<{
        comp: boolean;
        runsys: boolean;
        oil: boolean;
        refrigerant: boolean;
        suction: string;
        discharge: string;
        ampheater: string;
        loaded1: string;
        loaded2: string;
        loaded3: string;
        fan1: string;
        fan2: string;
        fan3: string;
        fan4: string;
        fan5: string;
        fan6: string;
    }>;
}

const CompressorSection: React.FC<CompressorSectionProps> = ({ compressors }) => {
    const Checkbox = ({ checked }: { checked: boolean }) => (
        <View style={styles.checkboxContainer}>
            <Text style={{ backgroundColor: '#000000', color: '#ffffff', fontSize: 6 }}>
                {checked ? 'X' : ''}
            </Text>
        </View>
    );

    const formatMeasurement = (value: string) => {
        return value && value.trim() !== '' ? value : 'N/A';
    };

    // Filter out empty compressors
    const validCompressors = compressors.filter((comp, index) =>
        comp.runsys || comp.oil || comp.refrigerant ||
        comp.suction || comp.discharge || comp.ampheater ||
        comp.loaded1 || comp.loaded2 || comp.loaded3 ||
        comp.fan1 || comp.fan2 || comp.fan3 || comp.fan4 || comp.fan5 || comp.fan6
    );

    if (validCompressors.length === 0) {
        return (
            <View style={styles.section}>
                <Text style={styles.sectionTitle}>Compressor Details</Text>
                <Text style={styles.noDataText}>No compressor data recorded</Text>
            </View>
        );
    }

    return (
        <View style={styles.section}>
            <Text style={styles.sectionTitle}>Compressor Details</Text>
            {validCompressors.map((compressor, index) => (
                <View key={index}>
                    <Text style={styles.compressorHeader}>Compressor {index + 1}</Text>

                    {/* Checklist Items - Horizontal Layout */}
                    <View style={styles.checklistGrid}>
                        <View style={styles.checklistItemHorizontal}>
                            <Checkbox checked={compressor.runsys} />
                            <Text style={styles.checkboxLabel}>Run System</Text>
                        </View>
                        <View style={styles.checklistItemHorizontal}>
                            <Checkbox checked={compressor.oil} />
                            <Text style={styles.checkboxLabel}>Check Oil Level</Text>
                        </View>
                        <View style={styles.checklistItemHorizontal}>
                            <Checkbox checked={compressor.refrigerant} />
                            <Text style={styles.checkboxLabel}>Check Refrigerant Level</Text>
                        </View>
                    </View>

                    {/* Measurements Grid */}
                    <View style={styles.measurementsGrid}>
                        <View style={styles.measurementItem}>
                            <Text style={styles.measurementLabel}>Suction:</Text>
                            <Text style={styles.measurementValue}>{formatMeasurement(compressor.suction)}</Text>
                        </View>
                        <View style={styles.measurementItem}>
                            <Text style={styles.measurementLabel}>Discharge:</Text>
                            <Text style={styles.measurementValue}>{formatMeasurement(compressor.discharge)}</Text>
                        </View>
                        <View style={styles.measurementItem}>
                            <Text style={styles.measurementLabel}>Amp/Heater:</Text>
                            <Text style={styles.measurementValue}>{formatMeasurement(compressor.ampheater)}</Text>
                        </View>
                        <View style={styles.measurementItem}>
                            <Text style={styles.measurementLabel}>Loaded 1:</Text>
                            <Text style={styles.measurementValue}>{formatMeasurement(compressor.loaded1)}</Text>
                        </View>
                        <View style={styles.measurementItem}>
                            <Text style={styles.measurementLabel}>Loaded 2:</Text>
                            <Text style={styles.measurementValue}>{formatMeasurement(compressor.loaded2)}</Text>
                        </View>
                        <View style={styles.measurementItem}>
                            <Text style={styles.measurementLabel}>Loaded 3:</Text>
                            <Text style={styles.measurementValue}>{formatMeasurement(compressor.loaded3)}</Text>
                        </View>
                        <View style={styles.measurementItem}>
                            <Text style={styles.measurementLabel}>Fan 1:</Text>
                            <Text style={styles.measurementValue}>{formatMeasurement(compressor.fan1)}</Text>
                        </View>
                        <View style={styles.measurementItem}>
                            <Text style={styles.measurementLabel}>Fan 2:</Text>
                            <Text style={styles.measurementValue}>{formatMeasurement(compressor.fan2)}</Text>
                        </View>
                        <View style={styles.measurementItem}>
                            <Text style={styles.measurementLabel}>Fan 3:</Text>
                            <Text style={styles.measurementValue}>{formatMeasurement(compressor.fan3)}</Text>
                        </View>
                        <View style={styles.measurementItem}>
                            <Text style={styles.measurementLabel}>Fan 4:</Text>
                            <Text style={styles.measurementValue}>{formatMeasurement(compressor.fan4)}</Text>
                        </View>
                        <View style={styles.measurementItem}>
                            <Text style={styles.measurementLabel}>Fan 5:</Text>
                            <Text style={styles.measurementValue}>{formatMeasurement(compressor.fan5)}</Text>
                        </View>
                        <View style={styles.measurementItem}>
                            <Text style={styles.measurementLabel}>Fan 6:</Text>
                            <Text style={styles.measurementValue}>{formatMeasurement(compressor.fan6)}</Text>
                        </View>
                    </View>
                </View>
            ))}
        </View>
    );
};

export default CompressorSection;
